import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {getWindow} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import withConfig from 'config/withConfig';
import {push} from 'store/router/router-actions';
import {routeToSearch} from 'router/router-utils';
import {
    setTerm,
    updateTimes,
} from 'store/search-request/search-request-actions';
import {resetSelectedSpot} from 'store/spot/spot-actions';
import {cancelSpotRequests} from 'store/spots/spots-actions';
import SearchGeocoderUtils from 'utils/search-geocoder-utils';
import SearchAPI from 'api/search';
import {Page} from 'utils/page-utils';
import pick from 'lodash/pick';
import SHUtils from 'utils/sh-utils';

const window = getWindow();
const DidYouMeanModal = loadable(() => import('../did-you-mean-modal'));

export default EnhancedComponent => {
    class WithSearchControls extends Component {
        static propTypes = {
            isMobile: PropTypes.bool,
            apiDateTimeFormat: PropTypes.string.isRequired,
            timezone: PropTypes.string,
            isAdmin: PropTypes.bool.isRequired,
            isMonthly: PropTypes.bool,
            isEvent: PropTypes.bool,
            isAirport: PropTypes.bool,
            isDefaultTimes: PropTypes.bool,
            starts: PropTypes.string,
            ends: PropTypes.string,
            searchString: PropTypes.string,
            searchBounds: PropTypes.shape({
                swLat: PropTypes.number,
                swLng: PropTypes.number,
                neLat: PropTypes.number,
                neLng: PropTypes.number,
            }),
            originalSearchString: PropTypes.string,
            usersLatitude: PropTypes.string,
            usersLongitude: PropTypes.string,
            cancelAllSpotRequests: PropTypes.func.isRequired,
            clearSelectedSpot: PropTypes.func.isRequired,
            updateSearchTimes: PropTypes.func.isRequired,
            pushTo: PropTypes.func.isRequired,
            onSearchClick: PropTypes.func,
            onClosePanelClick: PropTypes.func,
            setSearchTerm: PropTypes.func,
        };
        static defaultProps = {
            searchString: '',
            starts: null,
            ends: null,
        };

        constructor(props) {
            super(props);

            const {isMonthly, isAirport, searchString, starts, ends} = props;

            this._gmaps = EnvironmentUtils.isBrowser()
                ? window.google.maps
                : null;
            this._queuedLocationData = null;

            this.state = {
                starts,
                ends,
                isValidStart: true,
                isSearching: false,
                hasChangedDateTime: false,
                hasChangedDestination: false,
                currentSearch: searchString,
                searchAddressModalResults: null,
                prevIsMonthly: isMonthly,
                prevIsAirport: isAirport,
            };
        }

        static getDerivedStateFromProps(nextProps, prevState) {
            const {isMonthly, isAirport} = nextProps;
            const {prevIsMonthly, prevIsAirport} = prevState;
            let nextState = null;

            if (isMonthly !== prevIsMonthly || isAirport !== prevIsAirport) {
                nextState = {
                    ...nextState,
                    searchAddressModalResults: null,
                    prevIsMonthly: isMonthly,
                    prevIsAirport: isAirport,
                };
            }

            return nextState;
        }

        _onInputChange = value => {
            this.setState({
                currentSearch: value,
                hasChangedDestination: true,
            });
        };

        _onDateTimeChange = ({start, end, isValidStart}) => {
            const {apiDateTimeFormat, isDefaultTimes} = this.props;
            const {starts, ends} = this.state;
            const newStarts = start.format(apiDateTimeFormat);
            const newEnds = end.format(apiDateTimeFormat);
            const nextState = {
                starts: newStarts,
                ends: newEnds,
                isValidStart,
            };

            if (!isDefaultTimes || starts !== newStarts || ends !== newEnds) {
                nextState.hasChangedDateTime = true;
            }

            this.setState(nextState);
        };

        _onNoAddressFoundModalHidden = result => {
            const {onClosePanelClick, onSearchClick} = this.props;
            const {starts, ends} = this.state;

            this.setState({
                searchAddressModalResults: null,
            });

            // if multiple results are shown in the dialog then clicking a result should fire the selection process
            if (!isUndefined(result)) {
                this._onSearchAddressSelected(result);

                if (onSearchClick) {
                    onSearchClick({
                        starts,
                        ends,
                    });
                }
            }

            if (onClosePanelClick) {
                onClosePanelClick();
            }
        };

        _onSearchAddressSelected = results => {
            let data = results;

            if (isArray(results)) {
                if (results.length === 1) {
                    data = SearchGeocoderUtils.getAddressData(results[0]);
                } else {
                    data = map(data, result => {
                        return SearchGeocoderUtils.getAddressData(result);
                    });

                    // no results or more than one result found, show modal
                    this.setState({
                        searchAddressModalResults: data,
                    });

                    return;
                }
            }

            /* eslint-disable camelcase */
            const submitParams = {
                search_string: data.search_string,
                latitude: data.latitude,
                longitude: data.longitude,
                google_places_id: data.places_id,
                google_places_place_id: data.places_place_id,
                google_places_reference: data.places_reference,
            };
            /* eslint-enable camelcase */

            this._onSubmit(submitParams);
        };

        _onFindParkingNearMe = () => {
            const {pushTo, isMonthly} = this.props;
            const searchUrl = `/${isMonthly ? 'monthly-' : ''}parking-near-me/`;

            pushTo(searchUrl);
        };

        _onSearchClick = () => {
            const {onSearchClick, updateSearchTimes} = this.props;
            const {
                starts,
                ends,
                hasChangedDateTime,
                hasChangedDestination,
                currentSearch,
            } = this.state;

            if (hasChangedDestination) {
                const geocoder = new this._gmaps.Geocoder();

                geocoder.geocode(
                    {
                        address: currentSearch,
                    },
                    (res, status) => {
                        let results = res;

                        if (status !== this._gmaps.GeocoderStatus.OK) {
                            results = [];
                        }

                        this._onSearchAddressSelected(results);
                    }
                );
            }

            if (onSearchClick) {
                const searchClickParams = {
                    starts,
                    ends,
                };

                if (hasChangedDateTime) {
                    searchClickParams.callback = () => {
                        updateSearchTimes({
                            starts,
                            ends,
                        });
                    };
                }

                onSearchClick(searchClickParams);
            } else if (hasChangedDateTime) {
                updateSearchTimes({
                    starts,
                    ends,
                });
            }

            this.setState({
                hasChangedDateTime: false,
                hasChangedDestination: false,
            });
        };

        _onSubmit = async (locationData, isFromSuggestSelection = false) => {
            const {
                isMonthly,
                originalSearchString,
                usersLatitude,
                usersLongitude,
                cancelAllSpotRequests,
                clearSelectedSpot,
                pushTo,
                isDefaultTimes,
            } = this.props;
            const {starts, ends, hasChangedDateTime} = this.state;

            if (isFromSuggestSelection) {
                // save the location data and don't run a search because the user has to click Update Search in the UI
                this._queuedLocationData = locationData;

                return;
            }

            /* eslint-disable camelcase */
            const params = {
                ...(this._queuedLocationData
                    ? this._queuedLocationData
                    : locationData),
                csrfmiddlewaretoken: StorageUtils.get('csrftoken', 'cookie'),
                monthly: isMonthly,
                original_search_string: originalSearchString,
                users_latitude: usersLatitude,
                users_longitude: usersLongitude,
                starts: isDefaultTimes && !hasChangedDateTime ? null : starts,
                ends: isDefaultTimes && !hasChangedDateTime ? null : ends,
            };
            /* eslint-enable camelcase */

            this.setState({
                isSearching: true,
            });

            cancelAllSpotRequests();

            this._queuedLocationData = null;

            this.setState({
                isSearching: false,
                hasChangedDateTime: false,
                hasChangedDestination: false,
            });

            clearSelectedSpot();

            const {
                data: {data: searchParams},
            } = await SearchAPI.cleanParams(params);
            const {
                page_info: {
                    page_type: pageType,
                    setup: {
                        city: {id: cityId} = {},
                        destination: {id: destinationId} = {},
                    },
                },
            } = searchParams;
            const id =
                pageType === Page.CITY
                    ? cityId
                    : pageType === Page.DESTINATION
                    ? destinationId
                    : null;

            routeToSearch({
                method: pushTo,
                pageType,
                id,
                queryParams: {
                    ...(isMonthly && {monthly: true}),
                    ...(!id &&
                        pick(params, [
                            'latitude',
                            'longitude',
                            'search_string',
                        ])),
                    starts: searchParams.starts,
                    ends: isMonthly ? null : searchParams.ends,
                },
                historyState: {searchParams},
            });
        };

        _onNotLookingForEvent = () => {
            const {city, destination} = this.state;
            const {setSearchTerm} = this.props;

            setSearchTerm({
                term: 'daily',
                city,
                destination,
            });

            SHUtils.staticScrollTo({delay: 0});
        };

        render() {
            const {
                isSearching,
                isValidStart,
                searchAddressModalResults,
                currentSearch,
            } = this.state;

            return (
                <>
                    <EnhancedComponent
                        {...this.props}
                        isSearching={isSearching}
                        isValidStart={isValidStart}
                        currentSearch={currentSearch}
                        onInputChange={this._onInputChange}
                        onDateTimeChange={this._onDateTimeChange}
                        onFindParkingNearMe={this._onFindParkingNearMe}
                        onSearchClick={this._onSearchClick}
                        onNotLookingForEvent={this._onNotLookingForEvent}
                        onSubmit={this._onSubmit}
                    />
                    {searchAddressModalResults && (
                        <DidYouMeanModal
                            results={searchAddressModalResults}
                            onHidden={this._onNoAddressFoundModalHidden}
                        />
                    )}
                </>
            );
        }
    }

    const mapStateToProps = ({
        city: {data: city},
        event: {data: event},
        destination: {data: destination},
        search: {
            data: {isDefaultTimes},
        },
        searchRequest: {
            monthly,
            airport,
            event: isEvent,
            starts,
            ends,
            search_string: searchString,
            original_search_string: originalSearchString,
            users_latitude: usersLatitude,
            users_longitude: usersLongitude,
            pageType,
        },
        user: {
            data: {isAdmin},
        },
    }) => {
        const {
            display_name: cityDisplayName,
            state: displayState,
            search_bounds: searchBounds,
            timezone,
        } = city;
        let derivedSearchString = searchString ? searchString : '';

        if (isEmpty(derivedSearchString)) {
            if (pageType === 'city') {
                derivedSearchString = `${cityDisplayName}, ${displayState}`;
            } else if (pageType === 'destination' || pageType === 'event') {
                derivedSearchString = `${destination.title}, ${cityDisplayName}, ${displayState}`;
            }
        }

        return {
            isAdmin,
            timezone,
            isDefaultTimes,
            isMonthly: monthly,
            isAirport: airport,
            isEvent,
            starts,
            ends,
            searchString: derivedSearchString,
            originalSearchString,
            usersLatitude,
            usersLongitude,
            searchBounds: searchBounds && {
                swLat: searchBounds.sw_lat,
                swLng: searchBounds.sw_lng,
                neLat: searchBounds.ne_lat,
                neLng: searchBounds.ne_lng,
            },
        };
    };
    const mapDispatchToProps = {
        updateSearchTimes: updateTimes,
        cancelAllSpotRequests: cancelSpotRequests,
        clearSelectedSpot: resetSelectedSpot,
        setSearchTerm: setTerm,
        pushTo: push,
    };
    const enhance = compose(
        withConfig(['apiDateTimeFormat', 'isMobile']),
        connect(mapStateToProps, mapDispatchToProps)
    );

    return enhance(WithSearchControls);
};
